<script setup lang="ts">
import IconUpload from "@/assets/icons/IconUpload.vue";
import {ref} from "vue";

interface Props {
  acceptedFormats?: string;
  maxSize?: string;
}

withDefaults(defineProps<Props>(), {
  acceptedFormats: "XLS",
  maxSize: "25 Мб"
});

const emit = defineEmits<{
  upload: [files: FileList];
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);

const processFiles = (files: FileList) => {
  const modifiedFiles = Array.from(files).map(file => {
    return Object.assign(file, {
      file_name: file.name,
      file_size: file.size
    });
  });
  const dataTransfer = new DataTransfer();
  modifiedFiles.forEach(file => dataTransfer.items.add(file));
  emit("upload", dataTransfer.files);
};

const handleClick = () => {
  inputRef.value?.click();
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    processFiles(target.files);
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;

  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    processFiles(event.dataTransfer.files);
  }
};
</script>

<template>
  <div
    class="upload-box"
    :class="{ 'upload-box--dragging': isDragging }"
    @click="handleClick"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div class="upload-box__icon">
      <IconUpload />
    </div>
    <span class="upload-box__title">
      Перетащите файлы сюда или
      <span class="upload-box__title--active">нажмите, что бы выбрать</span>
    </span>
    <div class="upload-box__info">
      <span class="upload-box__subtitle">Поддерживаемый формат: {{ acceptedFormats }}</span>
      <span class="upload-box__subtitle">Макс. размер: {{ maxSize }}</span>
    </div>
    <input
      ref="inputRef"
      style="display: none"
      type="file"
      accept=".xls,.xlsx"
      multiple
      @change="handleFileChange"
    >
  </div>
</template>

<style scoped lang="scss">
.upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 20px;
  background-color: var(--background-01);
  border-radius: 10px;
  height: 184px;
  gap: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &--dragging {
    background-color: var(--background-bg-selected2);
    opacity: 0.8;
  }

  &__icon {
    width: 40px;
    height: 40px;
    background-color: var(--background-bg-white);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      width: 20px;
      height: 20px;
      color: var(--blue-blue80)
    }
  }

  &__title {
    color: var(--black-black90);
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    text-align: center;

    &--active {
      color: var(--background-bg-selected2);
    }
  }

  &__subtitle {
    color: var(--gray-gray90);
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
  }
}
</style>